import * as React from 'react'
import { useCallback, useState } from 'react'
import { Chip } from 'react-native-elements'
import { ScaledSheet } from 'react-native-size-matters'
import { Text } from '~components/Themed'
import { useTheme } from '~theme/ThemeManager'
import { Tag } from '~types'
import { FormInput } from './FormInput'
import { getModalStyleDefinition } from './ModalScreen'
import { View } from 'react-native'

const isTagValid = (value: string, minLength: number, maxLength: number) => {
  const regex = new RegExp(`^[\\w ]{${minLength},${maxLength}}$`, 'i')
  return regex.test(value)
}

interface Props {
  selected: Tag[]
  suggestions: Tag[]
  onAdd: (tag: Tag) => void
  onDelete: (index: number) => void
  onInput?: (value: string) => void
  searchOnly?: boolean
  splitWords?: boolean
  minLength?: number
  maxLength?: number
  maxSuggestions?: number
  prefixAdd?: string
  prompt?: string
}

export default function TagsInput({
  onAdd,
  onDelete,
  selected,
  suggestions,
  onInput,
  searchOnly = false,
  splitWords = true,
  minLength = 4,
  maxLength = 20,
  maxSuggestions = 20,
  prefixAdd = 'Add',
  prompt,
}: Props): React.JSX.Element {
  const [input, setInput] = useState('')
  const [filtered, setFiltered] = useState<Tag[]>(suggestions)

  const { theme } = useTheme()
  const styles = useStyles()

  // -----------------------------
  // Input / Suggestion Filtering
  // -----------------------------

  const handleInput = (value: string) => {
    setInput(value)
    onInput?.(value)

    if (!value) {
      setFiltered(suggestions)
      return
    }

    const f =
      suggestions
        ?.filter(
          (s) =>
            s.label.toLowerCase().includes(value.toLowerCase()) &&
            !selected.some((t) => t.value === s.value),
        )
        .slice(0, 8) ?? []

    setFiltered(f)
  }

  React.useEffect(() => {
    if (!input) {
      setFiltered(suggestions)
    } else {
      setFiltered(
        suggestions.filter((s) => s.label.toLowerCase().includes(input.toLowerCase())).slice(0, 8),
      )
    }
  }, [suggestions, input])

  // ----------------------------
  // Add Tags
  // ----------------------------
  const addTags = (label: string, value?: string) => {
    const rawTags = splitWords
      ? label
          .split(/[,\s]+/)
          .map((t) => t.trim())
          .filter(Boolean)
      : [value ?? label.trim()]

    const newTags = rawTags.filter((tag) => {
      const lower = tag.toLowerCase()
      const isNewTag = !suggestions.some((s) => {
        const suggestionValue = s.value.toLowerCase()
        const suggestionLabel = s.label.toLowerCase()
        return suggestionValue === lower || suggestionLabel === lower
      })

      if (searchOnly && isNewTag) return false

      return isTagValid(tag, minLength, maxLength) && !selected.some((t) => t.value === tag)
    })

    if (newTags.length === 0) return

    newTags.forEach((value) => onAdd({ label: value, value }))
    setInput('')
    setFiltered([])
  }

  // ----------------------------
  // Delete Tag
  // ----------------------------

  const onDeleteTag = useCallback(
    (tagToDelete: Tag) => {
      const index = selected.findIndex(
        (tag) => tag.value === tagToDelete.value || tag.label === tagToDelete.label,
      )
      if (index !== -1) {
        onDelete(index)
      }
    },
    [selected, onDelete],
  )

  const FilteredList = () => {
    const noMatches =
      !searchOnly &&
      filtered.length === 0 &&
      input.trim().length >= minLength &&
      input.trim().length <= maxLength &&
      !suggestions.some((s) => s.label.toLowerCase() === input.trim().toLowerCase())
    return (
      <>
        {(filtered.length > 0 || noMatches) && (
          <View style={[styles.tagSuggestionsStyle, { flexDirection: 'row', flexWrap: 'wrap' }]}>
            {filtered.slice(0, maxSuggestions).map((item: Tag) => (
              <Chip
                containerStyle={styles.tagContainer}
                buttonStyle={[styles.tag, styles.tagActive]}
                key={`tag-${String(item.value)}`}
                onPress={() => addTags(item.label, item.value)}
                title={
                  <Text style={styles.tagTitle}>
                    {prefixAdd}{' '}
                    <Text style={{ fontWeight: 'bold', color: theme.white }}>{item.label}</Text>
                  </Text>
                }
              />
            ))}
            {noMatches && (
              <Chip
                containerStyle={styles.tagContainer}
                buttonStyle={[styles.tag, styles.tagActive]}
                onPress={() => addTags(input)}
                title={
                  <Text style={styles.tagTitle}>
                    Create <Text style={{ fontWeight: 'bold', color: theme.white }}>{input}</Text>
                  </Text>
                }
              />
            )}
          </View>
        )}
      </>
    )
  }

  const placeholderDefault = searchOnly
    ? 'Search for an existing tag'
    : 'Search for or create a new tag'

  return (
    <View>
      <Text style={styles.label}>Folder Tags</Text>
      <View style={styles.tagContainerStyle}>
        {selected.map((tag) => (
          <Chip
            containerStyle={styles.tagContainer}
            buttonStyle={[styles.tag, styles.tagActive]}
            titleStyle={styles.tagTitle}
            title={tag.label}
            key={`tag-${tag.label}`}
            onPress={() => onDeleteTag(tag)}
            icon={{
              name: 'close',
              type: 'material',
              size: 14,
              color: theme.white,
              onPress: () => onDeleteTag(tag),
            }}
            iconRight
          />
        ))}
        <FormInput
          value={input}
          onChangeText={handleInput}
          onSubmitEditing={() => addTags(input)}
          placeholder={prompt ?? placeholderDefault}
          style={styles.tagInput}
        />
      </View>
      <FilteredList />

      {!searchOnly && (
        <Text style={styles.tagDescription}>
          Tags must be between 4 and 20 characters in length and contain only letters A-Z.
        </Text>
      )}
    </View>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    label: {
      fontSize: '10@ms0.3',
      fontWeight: '500',
      marginBottom: '3@mvs',
      color: theme[500],
    },
    tagInput: {
      width: '100%',
    },
    tagDescription: {
      fontSize: '8@ms0.4',
      fontWeight: '200',
    },
    tagContainer: {
      marginEnd: '4@ms',
      marginVertical: '2@ms',
    },
    tag: {
      backgroundColor: theme[400],
      paddingVertical: '6@mvs',
      paddingHorizontal: '4@ms',
    },
    tagButton: {
      borderRadius: '30px',
    },
    tagActive: {
      backgroundColor: theme.primary,
    },
    tagTitle: {
      fontSize: '10@ms0.2',
      color: theme.white,
    },
    tagSuggestionsStyle: {
      marginBottom: '16@mvs',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4@mvs',
    },
    tagContainerStyle: {
      marginVertical: '8@mvs',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4@mvs',
    },
  })
}
